import { isOneTrust } from './cmpFlag';
import { hasTargetingConsent } from './otBridge';

// Injects the OneTrust CMP SDK, but only when the CMP flag is on — so a build
// with REACT_APP_CMP unset behaves exactly as today (no OneTrust, no double
// banner) even if REACT_APP_ONETRUST_ID happens to be set.
//
// AutoBlock is intentionally OFF: trackers are gated manually via
// applyOneTrustTrackers() (src/consent/consent.js). OptanonWrapper is defined
// before the stub loads; the SDK calls it on load and after every consent
// change to drive Google Consent Mode and emit 'ot-consent-ready' for the React
// bridge (ConsentContext / OneTrustGate).
export function loadOneTrust() {
  if (!isOneTrust()) {return;}
  const domainScript = process.env.REACT_APP_ONETRUST_ID;
  if (!domainScript) {return;} // flag on but no id => misconfigured; do nothing rather than load a broken banner
  if (document.querySelector('script[data-domain-script]')) {return;} // idempotent — never inject a second banner

  window.OptanonWrapper = function OptanonWrapper() {
    const groups = ',' + (window.OnetrustActiveGroups || '') + ',';
    const has = (id) => groups.indexOf(',' + id + ',') !== -1;
    if (typeof window.gtag === 'function') {
      // ad_* signals honor GPC (sale/share opt-out), not just the raw C0004 group, so
      // client Consent Mode agrees with the server ad macros (see otBridge.isOptedOut).
      // analytics_storage is C0002 only — GPC is a sale/share signal, not an analytics one.
      const adConsent = hasTargetingConsent() ? 'granted' : 'denied';
      window.gtag('consent', 'update', {
        analytics_storage: has('C0002') ? 'granted' : 'denied',
        ad_storage: adConsent,
        ad_user_data: adConsent,
        ad_personalization: adConsent,
      });
    }
    window.dispatchEvent(new CustomEvent('ot-consent-ready', { detail: window.OnetrustActiveGroups }));
  };

  const preconnect = document.createElement('link');
  preconnect.rel = 'preconnect';
  preconnect.href = 'https://cdn.cookielaw.org';
  document.head.appendChild(preconnect);

  const stub = document.createElement('script');
  stub.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
  stub.type = 'text/javascript';
  stub.charset = 'UTF-8';
  stub.setAttribute('data-domain-script', domainScript);
  document.head.appendChild(stub);

  // US Privacy (IAB) script — defines window.__uspapi and the `usprivacy`
  // cookie. The OneTrust template only auto-loads GPP (otGPP.js), not US
  // Privacy, so without this the legacy CCPA signal is absent (FLLR audit
  // WEB-05/WEB-06). Same cdn.cookielaw.org origin as the stub, so no CSP
  // change is needed. Attributes are the auditor-prescribed values:
  //   ccpa-opt-out-ids="C0004"  -> opt out of the Targeting Cookies group
  //   ccpa-opt-out-geo="all"    -> emit the signal for all US visitors (the
  //                                service is US-only, so "all" == US-only)
  //   ccpa-opt-out-lspa="true"  -> LSPA-covered. This is the 4th char of the
  //     usprivacy string, set here in code (otCCPAiab.js reads it from this
  //     attribute, NOT from the OneTrust template). "true" matches the FLLR
  //     audit checklist's expected result and the auditor-prescribed snippet,
  //     so an opted-out string reads "1YYY".
  const usp = document.createElement('script');
  usp.src = 'https://cdn.cookielaw.org/opt-out/otCCPAiab.js';
  usp.type = 'text/javascript';
  usp.charset = 'UTF-8';
  usp.setAttribute('ccpa-opt-out-ids', 'C0004');
  usp.setAttribute('ccpa-opt-out-geo', 'all');
  usp.setAttribute('ccpa-opt-out-lspa', 'true');
  document.head.appendChild(usp);
}
